// Ticket lists (open / closed / all) with search + filters by client, platform
// (brand) and status, plus sortable columns — including live timers: time since
// the ticket was opened, time to resolution, and SLA remaining/overdue ("en
// quema"). Each row links to the management detail view.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import {
  listTickets, getSettings, formatDate, formatMins, tsToDate,
  type Ticket, type ServiceDeskSettings, type SlaState,
} from "../services/serviceDeskService";
import { SlaBadge, PriorityBadge, StatusBadge, AiAgentBadge, ReworkChip } from "../components/Badges";
import { statusFallback } from "../services/ticketClosure";
import { SERVICE_DESK_BASE } from "../config";

type Scope = "open" | "closed" | "all";
type SortKey = "client" | "brand" | "status" | "priority" | "sla" | "age" | "resolution" | "created";

const PRIORITY_RANK: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };

// Per-ticket derived timers (minutes). null = not applicable.
interface Row extends Ticket {
  createdMs: number | null;
  ageMins: number | null;          // time since opened (or until resolution if closed)
  resolutionMins: number | null;   // time to resolution (resolved − created)
  slaRemainingMins: number | null; // due − now; negative = vencido / "en quema"
}

function buildRow(t: Ticket, now: number): Row {
  const createdMs = tsToDate(t.createdAt)?.getTime() ?? null;
  const endMs = tsToDate(t.resolvedAt)?.getTime() ?? tsToDate(t.closedAt)?.getTime() ?? null;
  const ageMins = createdMs != null ? Math.round(((endMs ?? now) - createdMs) / 60000) : null;
  const resolutionMins = createdMs != null && endMs != null ? Math.round((endMs - createdMs) / 60000) : null;
  const dueRes = t.slaStatus?.dueAt?.resolution ?? null;
  const slaRemainingMins = dueRes != null && !endMs ? Math.round((dueRes - now) / 60000) : null;
  return { ...t, createdMs, ageMins, resolutionMins, slaRemainingMins };
}

// Signed duration: "2 h 10 min" / "-1 d 4 h" (overdue). null → "—".
function signedMins(mins: number | null): { text: string; overdue: boolean } {
  if (mins == null) return { text: "—", overdue: false };
  const overdue = mins < 0;
  return { text: `${overdue ? "-" : ""}${formatMins(Math.abs(mins))}`, overdue };
}

export default function TicketsListSection() {
  const { t } = useTranslation("serviceDesk");
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<Scope>("open");
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [status, setStatus] = useState("");
  const [client, setClient] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    const params: Record<string, string | number> = { scope, limit: 200 };
    if (q) params.q = q;
    if (brand) params.brand = brand;
    if (status) params.status = status;
    const handle = setTimeout(() => {
      listTickets(params).then((r) => setTickets(r.data)).finally(() => setLoading(false));
    }, q ? 300 : 0);
    return () => clearTimeout(handle);
  }, [scope, q, brand, status]);

  const now = Date.now();

  // Distinct clients (requesters) present in the current result set.
  const clients = useMemo(() => {
    const names = new Set<string>();
    tickets.forEach((t) => { const n = t.requester?.name?.trim(); if (n) names.add(n); });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  const rows = useMemo(() => {
    let r = tickets.map((t) => buildRow(t, now));
    if (client) r = r.filter((t) => (t.requester?.name || "") === client);

    const val = (row: Row): string | number => {
      switch (sort.key) {
        case "client": return (row.requester?.name || "").toLowerCase();
        case "brand": return (row.brandLabel || row.brand || "").toLowerCase();
        case "status": return (row.statusLabel || row.status || "").toLowerCase();
        case "priority": return PRIORITY_RANK[row.priority] ?? 0;
        case "sla": return row.slaRemainingMins ?? Number.POSITIVE_INFINITY;
        case "age": return row.ageMins ?? -1;
        case "resolution": return row.resolutionMins ?? Number.POSITIVE_INFINITY;
        case "created": return row.createdMs ?? 0;
      }
    };
    return [...r].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va < vb) return -1 * sort.dir;
      if (va > vb) return 1 * sort.dir;
      return 0;
    });
  }, [tickets, client, sort, now]);

  // Filas paginadas
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return rows.slice(startIndex, endIndex);
  }, [rows, currentPage]);

  const totalPages = Math.ceil(rows.length / itemsPerPage);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: key === "created" || key === "age" ? -1 : 1 }));

  const statusColor = (id: string) => (settings?.statuses || []).find((s) => s.id === id)?.color ?? statusFallback(id)?.color;
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";

  const selectCls = `rounded-lg border px-3 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white" : "!bg-white !border-gray-300"}`;

  const scrollbarStyles = `
    .tickets-scroll::-webkit-scrollbar {
      height: 12px;
      width: 12px;
    }
    .tickets-scroll::-webkit-scrollbar-track {
      background: ${isDarkMode ? "#1f2937" : "#f1f5f9"};
      border-radius: 6px;
    }
    .tickets-scroll::-webkit-scrollbar-thumb {
      background: ${isDarkMode ? "#4b5563" : "#cbd5e1"};
      border-radius: 6px;
      border: 2px solid ${isDarkMode ? "#1f2937" : "#f1f5f9"};
    }
    .tickets-scroll::-webkit-scrollbar-thumb:hover {
      background: ${isDarkMode ? "#6b7280" : "#94a3b8"};
    }
  `;

  return (
    <div className="p-6">
      <style>{scrollbarStyles}</style>
      <h1 className={`mb-4 text-xl font-bold ${text}`}>{t("ticketsList.title")}</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className={`flex rounded-lg border p-0.5 ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
          {(["open", "closed", "all"] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`rounded-md px-3 py-1 text-sm font-medium ${scope === s ? "bg-primary-500 text-white" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {s === "open" ? t("ticketsList.open") : s === "closed" ? t("ticketsList.closed") : t("ticketsList.all")}
            </button>
          ))}
        </div>
        <input
          className={`flex-1 min-w-[200px] rounded-lg border px-3 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white placeholder:!text-gray-400" : "!bg-white !border-gray-300 placeholder:!text-gray-500"}`}
          placeholder={t("ticketsList.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className={selectCls} value={client} onChange={(e) => setClient(e.target.value)}>
          <option value="">{t("ticketsList.allClients")}</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectCls} value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">{t("ticketsList.allPlatforms")}</option>
          {(settings?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
        <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("ticketsList.allStatuses")}</option>
          {(settings?.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      <div className={`rounded-xl border ${card}`}>
        <div className="tickets-scroll overflow-x-auto">
          {loading ? (
            <p className="p-8 text-center text-gray-400">{t("ticketsList.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-gray-400">{t("ticketsList.noTickets")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className={isDarkMode ? "bg-dark-bg" : "bg-gray-50"}>
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="px-4 py-2">{t("ticketsList.table.reference")}</th>
                  <th className="px-4 py-2">{t("ticketsList.table.subject")}</th>
                  <Th label={t("ticketsList.table.client")} col="client" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.platform")} col="brand" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.status")} col="status" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.priority")} col="priority" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.sla")} col="sla" sort={sort} onSort={toggleSort} title={t("ticketsList.table.slaTitle")} />
                  <Th label={t("ticketsList.table.openedAgo")} col="age" sort={sort} onSort={toggleSort} title={t("ticketsList.table.openedAgoTitle")} />
                  <Th label={t("ticketsList.table.resolution")} col="resolution" sort={sort} onSort={toggleSort} title={t("ticketsList.table.resolutionTitle")} />
                  <Th label={t("ticketsList.table.created")} col="created" sort={sort} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((ticket) => {
                const sla = signedMins(ticket.slaRemainingMins);
                return (
                  <tr
                    key={ticket.id}
                    onClick={() => navigate(`${SERVICE_DESK_BASE}/tickets/${ticket.id}`)}
                    className={`cursor-pointer border-t ${isDarkMode ? "border-dark-border hover:bg-dark-bg" : "border-gray-100 hover:bg-gray-50"}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{ticket.reference}</td>
                    <td className={`px-4 py-2.5 font-medium ${text}`}>
                      {ticket.title}
                      {ticket.escalated && <span className="ml-2 text-[11px] font-semibold text-red-500">{t("ticketsList.table.escalated")}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{ticket.requester?.name || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500">{ticket.brandLabel}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <StatusBadge label={ticket.statusLabel || statusFallback(ticket.status)?.label || ticket.status} color={statusColor(ticket.status)} />
                        <ReworkChip count={ticket.rework?.count} />
                        <AiAgentBadge state={ticket.aiAgent?.state} />
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><PriorityBadge priority={ticket.priority} /></td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <SlaBadge state={(ticket.slaStatus?.overall || ticket.sla?.overall) as SlaState} />
                        {ticket.slaRemainingMins != null && (
                          <span className={`text-xs whitespace-nowrap ${sla.overdue ? "font-semibold text-red-500" : "text-gray-400"}`}>{sla.text}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatMins(ticket.ageMins)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatMins(ticket.resolutionMins)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{formatDate(ticket.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Controles de paginación fuera del scroll */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: isDarkMode ? "#374151" : "#e5e7eb" }}>
            <div className="text-sm text-gray-500">
              {t("ticketsList.pagination.showing", { 
                start: (currentPage - 1) * itemsPerPage + 1, 
                end: Math.min(currentPage * itemsPerPage, rows.length), 
                total: rows.length 
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-700"} ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                ← {t("ticketsList.pagination.previous")}
              </button>
              <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                {t("ticketsList.pagination.page", { current: currentPage, total: totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-700"} ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {t("ticketsList.pagination.next")} →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Sortable header cell. Shows a ▲/▼ caret on the active column.
function Th({ label, col, sort, onSort, title }: {
  label: string; col: SortKey; sort: { key: SortKey; dir: 1 | -1 }; onSort: (k: SortKey) => void; title?: string;
}) {
  const active = sort.key === col;
  return (
    <th className="px-4 py-2" title={title}>
      <button onClick={() => onSort(col)} className={`inline-flex items-center gap-1 uppercase ${active ? "text-gray-600" : ""}`}>
        {label}
        <span className="text-[10px]">{active ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}
